import type { ContractDateList } from "@closer/shared";
import { Button, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { relativeDateLabel } from "../../lib/relativeDate";
import { tenantKey } from "../../lib/tenantQuery";
import { ContractBasicsDialog } from "./ContractBasicsDialog";
import { ContractClientSection } from "./ContractClientSection";
import { ContractCriticalProcesses } from "./ContractCriticalProcesses";
import { ContractDateDialog } from "./ContractDateDialog";
import { useContractDetail } from "./contractDetailContext";
import { ContractMyPendingTasks } from "./ContractMyPendingTasks";
import { Bolum, Satir } from "./contractSectionCard";
import { ContractSummaryHeader } from "./ContractSummaryHeader";

/**
 * ÖZET bölümü — kaydın açılış ekranı.
 *
 * ### ⚠ Bu sayfanın tek sorusu var
 *
 * "Bu sözleşme nedir, nerede duruyor, benden ne bekleniyor?" Eskiden burası (Genel Bakış)
 * dokuz kart taşıyordu — künye, müvekkil, bağlı kayıtlar, iç notlar, kritik tarihler,
 * ekler, yükümlülükler, zaman çizelgesi, kritik süreçler — ve bunların yedisi boş durum
 * gösteriyordu. Sözleşmeyi açan kullanıcı, cevabını aradığı üç satırı bulmak için boş
 * kutuların arasından geçiyordu. İdari olanlar Kayıt bölümüne taşındı; burada yalnız
 * KİMLİK (künye, müvekkil) ve DURUM (süreçler, tarihler) kaldı.
 *
 * ### ⚠ Yükleme/hata kapısı KABUKTA
 *
 * Kayıt yoksa ya da erişim yoksa kabuk tek bir `StateView` çiziyor ve bu sayfa hiç
 * çizilmiyor. Bu yüzden künye kartı `durum`u "yüklendi" olarak veriyor: buraya gelindiyse
 * kayıt zaten elde. Kendi başına ikinci bir yükleme durumu göstermek, aynı bekleyişi iki
 * kez anlatmak olurdu.
 */
export function ContractOverviewPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { id, s, arsivde, kayitYazma } = useContractDetail();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [temelAcik, setTemelAcik] = useState(false);
  const [tarihAcik, setTarihAcik] = useState(false);

  const tarihler = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "tarihler"),
    queryFn: async () =>
      (await api<ContractDateList>(`/api/contracts/${id}/dates`, { tenantId })).data,
  });

  const turAdi = s.types.find((t) => t.code === s.contractType)?.label ?? null;

  /** Kabuk kaydı zaten yükledi; künye kartı ikinci bir bekleyiş göstermiyor. */
  const kunyeDurumu = { isPending: false, error: null };

  /**
   * Temel bilgileri güncelleme (CLOS-110 `[072]`).
   *
   * ⚠ `CONCURRENCY_CONFLICT` körlemesine tekrar GÖNDERİLMİYOR: kayıt yeniden okunuyor ve
   * kullanıcıya söyleniyor. Tekrar göndermek, başkasının değişikliğini ezmek olurdu
   * (apps/web/CLAUDE.md hata sözleşmesi).
   */
  const temelKaydet = useMutation({
    mutationFn: async (govde: Record<string, unknown>) =>
      (
        await api(`/api/contracts/${id}`, {
          method: "PATCH",
          tenantId,
          body: JSON.stringify(govde),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "detay"),
      });
      setTemelAcik(false);
      toast.success("Temel bilgiler güncellendi");
    },
    onError: async (error) => {
      if (error instanceof ApiRequestError && error.code === "CONCURRENCY_CONFLICT") {
        await queryClient.invalidateQueries({
          queryKey: tenantKey(tenantId, "contract", id, "detay"),
        });
        setTemelAcik(false);
        toast.error("Kayıt siz düzenlerken değişti; güncel hâli yüklendi");
        return;
      }
      toast.error(
        error instanceof ApiRequestError ? error.message : "Temel bilgiler güncellenemedi",
      );
    },
  });

  /** Kritik tarih ekleme (CLOS-113 `[075]`). */
  const tarihEkle = useMutation({
    mutationFn: async (govde: Record<string, unknown>) =>
      (
        await api(`/api/contracts/${id}/dates`, {
          method: "PUT",
          tenantId,
          body: JSON.stringify(govde),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "tarihler"),
      });
      setTarihAcik(false);
      toast.success("Tarih eklendi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Tarih eklenemedi");
    },
  });

  /** Kritik tarih kaldırma (CLOS-113 `[075]`). Yanlış girilen tarih tek tıkla geri eklenir. */
  const tarihKaldir = useMutation({
    mutationFn: async (dateId: string) =>
      (await api(`/api/contracts/${id}/dates/${dateId}`, { method: "DELETE", tenantId })).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "tarihler"),
      });
      toast.success("Tarih kaldırıldı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Tarih kaldırılamadı");
    },
  });

  return (
    <div className="flex flex-col gap-4">
      {/*
        ⚠ KRİTİK SÜREÇ ÖZETİ EN ÜSTTE (CLOS-1112 · rapor 5 §3).

        Kullanıcı "güncel sürümün süreçleri ne durumda?" sorusunun cevabını Süreç bölümünü
        açmadan görebilmeli — bu sayfanın tek sorusu zaten o. Panel SALT OKUNUR: rapor
        "kritik kararlar bu özet panelinde verilmez" diyor ve karar, sürecin kendi
        bölümünde veriliyor.
      */}
      {/*
        ⚠ "BENDEN BEKLENİYOR" EN ÜSTTE — Kritik Süreçler'in de üstünde.

        İkisi farklı soru cevaplıyor: Kritik Süreçler "bu sözleşmenin süreçleri ne
        durumda" (herkes için, süreç bazında), bu blok "BANA ne düşüyor" (kişiye özel,
        görev bazında). Kullanıcının sayfayı açma sebebi çoğunlukla ikincisi, o yüzden
        önce o.

        ⚠ Görev yoksa blok HİÇ çizilmiyor; boş bir kart bu sayfayı yeniden düzenleme
        sebebimizin ta kendisiydi.
      */}
      <ContractMyPendingTasks contractId={id} />

      <ContractCriticalProcesses contractId={id} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Bolum
          baslik="Temel bilgiler"
          durum={kunyeDurumu}
          /*
            Eylem YETKİYE ve KAYIT DÜZEYİNE bağlı: düğmeyi herkese gösterip 403/404
            duvarına toslatmıyoruz (CLOS-888).
          */
          eylem={
            <div className="flex items-center gap-2.5">
              {/*
                ⚠ "Düzenleme N" KİMLİK SATIRINDAN BURAYA İNDİ (5 Eylül).

                `s.version` bir BELGE sürümü değil, temel bilgi güncellemelerini sayan
                eşzamanlılık sayacı (`contracts.version`, `[050]` deseni). 4 Eylül turunda
                adı "Sürüm"den "Düzenleme"ye çevrilmişti, çünkü üç ayrı yerdeki üç farklı
                sayı aynı adı taşıyordu. Ad düzeldi ama YER yanlış kaldı: başlığın altında,
                kaydın numarası ve durumuyla aynı hizada duruyordu — oysa kullanıcı kaydı
                numarasıyla tanır, eşzamanlılık sayacıyla değil.

                Sayının anlamlı olduğu tek an, bu kartı DÜZENLERKEN başkasının araya
                girmesi. O yüzden düzenleme düğmesinin yanında ve yetkiden BAĞIMSIZ:
                düzenleyemeyen kullanıcı da hangi hâli gördüğünü bilmeli.
              */}
              <span className="text-xs text-fg-soft">Düzenleme {s.version}</span>
              {can("contract.edit") && kayitYazma ? (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setTemelAcik(true)}
                >
                  Temel bilgileri düzenle
                </Button>
              ) : null}
            </div>
          }
        >
          {/*
            UI Tur 2 §7.4: "Sözleşme Bilgileri" kartı DefinitionGrid 2 kolon. Spec'in
            listesi (Karşı Taraf, Sorumlu, Bölüm/Departman, Dil, Oluşturma/Güncelleme
            tarihleri, Oluşturan/Güncelleyen) BU sorgunun (`detay`) yükünde YOK — o
            alanlar Taraflar/Sorumlular bölümlerinin KENDİ sorgularında yaşıyor
            ("her bölüm kendi kanonik kaynağından okunur" kararı, bu dosyanın en üstünde).
            İkinci bir sorguya kopyalamak o kararı ihlal ederdi; kart yalnız GERÇEKTEN bu
            sorguda olan alanlarla sınırlı.
          */}
          {/*
              ⚠ TİRE KALKTI — canlı turda ölçüldü (4 Eylül). Yeni yüklenen bir docx'te bu
              kartın beş alanı da "—" gösteriyordu ve kullanıcı bunu "sistem dosyamı aldı
              ama hiçbir şey yapmadı" diye okuyordu. Tire, doldurulmuş bir alanla
              doldurulmamış bir alanı aynı görünüme sokuyor: bir EKSİK değil bir DEĞER
              gibi duruyor. Boş alanlar artık "Eksik" diye işaretleniyor ve kaç tanesinin
              eksik olduğu kartın başında tek cümlede yazıyor.

              ⚠ Yönlendirme cümlesi YETKİYE bağlı: düzeltme yolunu ancak düğmeyi
              gerçekten görebilen kullanıcıya söylüyoruz, yoksa olmayan bir düğmeye
              yollamış olurduk.
            */}
          <ContractSummaryHeader
            yonlendirme={
              can("contract.edit") && kayitYazma
                ? "Temel bilgileri düzenleyerek tamamlayabilirsiniz."
                : undefined
            }
            fields={[
              { key: "tur", label: "Tür", value: turAdi },
              /*
                ⚠ REFERANS NUMARASI YALNIZ EKSİKKEN BURADA.

                Numara artık kimlik satırında (başlığın altında, rozetin solunda) —
                kullanıcının kaydı dışarıya (e-posta, dilekçe, muhasebe) taşırken aradığı
                yer orası. İkisinde birden göstermek ÖLÇÜLÜR bir zarar: aynı metin iki
                düğüme yayılınca `getByText` iki eşleşme bulup kırılıyor (bu dosyanın
                bekçisi tam olarak böyle kırmızıya döndü) — kırıntı yolu da aynı gerekçeyle
                kaldırılmıştı.

                Ama alan listeden TAMAMEN çıkmıyor: kartın işi kimlik değil TAMLIK, ve
                numarası olmayan kayıtta kimlik satırı sessiz kalıyor. Eksikliği söyleyen
                tek yer bu kart olduğu için, yalnız eksikken listeye giriyor.
              */
              ...(s.referenceNumber
                ? []
                : [{ key: "referans", label: "Referans numarası", value: null }]),
              {
                key: "bedel",
                label: "Bedel",
                value: s.value ? `${s.value} ${s.currency ?? ""}`.trim() : null,
              },
              { key: "aciklama", label: "Açıklama", value: s.description },
              {
                key: "etiketler",
                label: "Etiketler",
                value: s.tags.length > 0 ? s.tags.join(", ") : null,
              },
            ]}
          />
        </Bolum>

        {/*
          ⚠ Müvekkil bölümü KÜNYENİN YANINDA (CLOS-824): "bu kimin sözleşmesi" sorusu
          kaydın kimliğinin parçası, idari bir detay değil.
        */}
        <ContractClientSection contractId={id} arsivde={arsivde} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Bolum
          baslik="Kritik tarihler"
          durum={tarihler}
          eylem={
            can("contract.edit") && kayitYazma ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setTarihAcik(true)}
              >
                Tarih ekle
              </Button>
            ) : null
          }
          bos={tarihler.data?.dates.length === 0}
          bosMetni="Henüz tarih girilmemiş."
        >
          <div className="flex flex-col">
            {(tarihler.data?.dates ?? []).map((t) => (
              <Satir
                key={t.id}
                /**
                 * ⚠ KULLANICI TANIMLI AD ÖNCE (CLOS-1193).
                 *
                 * Satır etiketi yalnız katalogdan geliyordu ve `t.label` hiç okunmuyordu.
                 * `multiple: true` türlerde ad, iki satırı ayıran TEK şey — üstelik
                 * `slot_key`'in de parçası. Ölçüldü: iki ayrı adla eklenen kritik tarih
                 * ekranda ikisi de "Kritik tarih" görünüyordu.
                 *
                 * ⚠ Ürün doğrusunu başka yerde zaten yapıyor (`home/sections.ts`); aynı
                 * ifade buraya taşındı.
                 */
                etiket={
                  t.label ??
                  tarihler.data?.types.find((x) => x.code === t.dateType)?.label ??
                  t.dateType
                }
                deger={
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span>
                      {t.precision === "day"
                        ? new Date(t.value).toLocaleDateString("tr-TR")
                        : new Date(t.value).toLocaleString("tr-TR")}
                    </span>
                    {/*
                      ⚠ GÖRELİ ETİKET MUTLAK TARİHİN YANINDA, yerine değil.

                      Kart yalnız "31.12.2028" gösteriyordu ve aciliyeti kullanıcı kafasında
                      hesaplıyordu — hedef tasarımın aynı yerinde ikisi birden var
                      ("15.08.2028 · 2 yıl kaldı"). Günün kendisi de gerekli: kullanıcı onu
                      takvime, dilekçeye, hatırlatmaya işliyor.

                      ⚠ Etiket üretilemezse (okunamaz değer) hiçbir şey çizilmiyor; boş bir
                      ayraç ya da "—" olmayan bir bilgiyi varmış gibi gösterirdi.
                    */}
                    {relativeDateLabel(t.value) ? (
                      <span className="text-xs text-fg-soft">{relativeDateLabel(t.value)}</span>
                    ) : null}
                  </span>
                }
                eylem={
                  can("contract.edit") && kayitYazma ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => tarihKaldir.mutate(t.id)}
                    >
                      Kaldır
                    </Button>
                  ) : null
                }
              />
            ))}
          </div>
        </Bolum>
      </div>

      <ContractBasicsDialog
        open={temelAcik}
        onClose={() => setTemelAcik(false)}
        mevcut={s}
        onConfirm={(govde) => temelKaydet.mutate(govde)}
        isPending={temelKaydet.isPending}
      />
      <ContractDateDialog
        open={tarihAcik}
        onClose={() => setTarihAcik(false)}
        types={tarihler.data?.types ?? []}
        onConfirm={(govde) => tarihEkle.mutate(govde)}
        isPending={tarihEkle.isPending}
      />
    </div>
  );
}
